import React from "react";

import {
  Link,
} from "react-router-dom";

import {
  motion,
} from "framer-motion";

import {
  useTranslation,
} from "react-i18next";

import {
  media,
} from "../../server";

import {
  useStateContext,
} from "../../context/ContextProvider";

import {
  renderText,
  truncateText,
} from "../hooks/TextVeiw";

import {
  truncateTitle,
} from "../../middleware/truncateTitle";

import {
  FiArrowLeft,
  FiArrowRight,
  FiImage,
} from "react-icons/fi";

import {
  FaUtensils,
} from "react-icons/fa";


function MealCategoryCard({
  category,
  index = 0,
  compact = false,
}) {
  const {
    currentDir,
  } = useStateContext();

  const {
    t,
  } = useTranslation();


  /* =====================================
      DIRECTION
  ====================================== */

  const isRTL =
    currentDir === "rtl";


  const DirectionArrow =
    isRTL
      ? FiArrowLeft
      : FiArrowRight;


  /* =====================================
      DATA
  ====================================== */

  const title =
    isRTL
      ? category?.titleAr
      : category?.titleEn;


  const description =
    isRTL
      ? category?.textAr
      : category?.textEn;


  const hasImage =
    Boolean(
      category?.Image
    );


  /* =====================================
      SCROLL HANDLER
  ====================================== */

  const handleScrollTop =
    () => {

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

    };


  return (
    <motion.article
      initial={{
        opacity: 0,
        y: 30,
      }}

      whileInView={{
        opacity: 1,
        y: 0,
      }}

      transition={{
        duration: 0.5,

        delay:
          Math.min(
            index * 0.05,
            0.25
          ),
      }}

      viewport={{
        once: true,
        amount: 0.12,
      }}

      whileHover={{
        y:
          compact
            ? -4
            : -7,
      }}

      className="
        group

        relative

        h-full
        min-w-0
      "
    >

      {/* =====================================
          CARD
      ====================================== */}

      <div
        className="
          relative

          flex
          flex-col

          h-full

          overflow-hidden

          rounded-[1.25rem]
          md:rounded-[2rem]

          bg-n-1
          dark:bg-back-darksecondary

          border
          border-color-2/10
          dark:border-white/10

          shadow-[0_10px_30px_rgba(68,37,20,0.07)]

          transition-all
          duration-500

          hover:border-color-1/35

          hover:shadow-[0_24px_60px_rgba(68,37,20,0.14)]
        "
      >

        {/* =================================
            IMAGE
        ================================= */}

        <div
          className={`
            relative

            shrink-0

            overflow-hidden

            ${
              compact
                ? `
                  h-[140px]
                  sm:h-[195px]

                  md:h-[280px]
                `
                : `
                  h-[220px]
                  sm:h-[250px]

                  md:h-[280px]
                `
            }
          `}
        >

          {/* =============================
              IMAGE / FALLBACK
          ============================== */}

          {hasImage ? (

            <img
              src={`${media}${category.Image}`}

              alt={
                title ||
                t(
                  "Yaman category"
                )
              }

              loading="lazy"

              className="
                w-full
                h-full

                object-cover

                transition-transform
                duration-[900ms]
                ease-out

                group-hover:scale-105
              "
            />

          ) : (

            <div
              className="
                flex

                w-full
                h-full

                items-center
                justify-center

                bg-back-secondary
                dark:bg-back-darkprimery

                text-color-1/35
              "
            >
              <FiImage
                className="
                  text-4xl
                  md:text-5xl
                "
              />
            </div>

          )}


          {/* =================================
              IMAGE OVERLAY
          ================================= */}

          <div
            className="
              absolute
              inset-0

              bg-gradient-to-t

              from-color-2/70
              via-color-2/[0.04]
              to-transparent

              pointer-events-none
            "
          />


          {/* =================================
              CATEGORY LABEL
          ================================= */}

          <div
            className={`
              absolute

              top-3
              start-3

              z-10

              inline-flex

              max-w-[70%]

              items-center

              rounded-full

              border
              border-white/20

              bg-color-2/55

              backdrop-blur-md

              text-white

              ${
                compact
                  ? `
                    gap-1

                    px-2
                    py-1.5

                    md:gap-2
                    md:px-4
                    md:py-2
                  `
                  : `
                    gap-1.5

                    px-3
                    py-2

                    md:px-4
                  `
              }
            `}
          >

            <FaUtensils
              className={`
                shrink-0

                text-color-1

                ${
                  compact
                    ? `
                      text-[8px]

                      md:text-sm
                    `
                    : `
                      text-[10px]

                      md:text-sm
                    `
                }
              `}
            />


            <span
              className={`
                truncate

                font-kufi

                ${
                  compact
                    ? `
                      text-[7px]

                      md:text-xs
                    `
                    : `
                      text-[9px]

                      md:text-xs
                    `
                }
              `}
            >
              {t(
                "Yaman category"
              )}
            </span>

          </div>


          {/* =================================
              CATEGORY NUMBER
          ================================= */}

          <div
            className={`
              absolute

              top-3
              end-3

              z-10

              flex

              shrink-0

              items-center
              justify-center

              rounded-full

              border
              border-white/20

              bg-black/15

              backdrop-blur-md

              ${
                compact
                  ? `
                    w-8
                    h-8

                    md:w-12
                    md:h-12
                  `
                  : `
                    w-10
                    h-10

                    md:w-12
                    md:h-12
                  `
              }
            `}
          >

            <span
              className={`
                font-kufi

                text-white/85

                ${
                  compact
                    ? `
                      text-[8px]

                      md:text-xs
                    `
                    : `
                      text-[10px]

                      md:text-xs
                    `
                }
              `}
            >
              {String(
                index + 1
              ).padStart(
                2,
                "0"
              )}
            </span>

          </div>


          {/* =================================
              GOLD ACCENT
          ================================= */}

          <div
            className="
              absolute

              bottom-0
              start-0

              z-10

              w-14
              md:w-20

              h-[3px]

              bg-color-1

              transition-all
              duration-500

              group-hover:w-full
            "
          />

        </div>


        {/* =================================
            CONTENT
        ================================= */}

        <div
          className={`
            flex
            flex-1
            flex-col

            ${
              compact
                ? `
                  p-3

                  md:p-7
                `
                : `
                  p-4
                  sm:p-5

                  md:p-7
                `
            }
          `}
        >

          {/* =================================
              TITLE
          ================================= */}

          <h3
            className={`
              font-kufi
              font-semibold

              text-tcolor-4
              dark:text-tcolor-1

              line-clamp-1

              transition-colors
              duration-300

              group-hover:text-color-1

              ${
                compact
                  ? `
                    text-md
                    leading-6

                    mb-2

                  

                    md:text-2xl
                    md:leading-normal
                    md:mb-4
                  `
                  : `
                    text-lg
                    leading-8

                    sm:text-xl

                    md:text-2xl

                    mb-3
                    sm:mb-4
                  `
              }
            `}
          >
            {truncateTitle(
              title,
              compact
                ? 3
                : 4
            )}
          </h3>


          {/* =================================
              DESCRIPTION

              هاتف + عمودان = مخفي

              هاتف + عمود واحد = ظاهر

              Desktop = ظاهر دائماً
          ================================= */}

          {description && (

            <p
              className={`
                font-kufi

                text-n-6
                dark:text-tcolor-2

                line-clamp-3

                ${
                  compact
                    ? `
                      hidden

                      md:block

                      md:min-h-[84px]
                      md:mb-6

                      md:text-sm
                      md:leading-7
                    `
                    : `
                      block

                      text-[11px]
                      leading-6

                      min-h-[54px]

                      mb-4

                      sm:text-sm
                      sm:leading-7
                      sm:min-h-[84px]
                      sm:mb-6
                    `
                }
              `}
            >
              {renderText(
                truncateText(
                  description,
                  compact
                    ? 18
                    : 24
                )
              )}
            </p>

          )}


          {/* =================================
              DIVIDER
          ================================= */}

          <div
            className={`
              mt-auto

              w-full
              h-px

              bg-color-2/10
              dark:bg-white/10

              ${
                compact
                  ? `
                    mb-3

                    md:mb-5
                  `
                  : `
                    mb-4
                    sm:mb-5
                  `
              }
            `}
          />


          {/* =================================
              CTA
          ================================= */}

          <Link
            to="/AllMeals"

            state={{
              category,
            }}

            onClick={
              handleScrollTop
            }

            aria-label={`${t(
              "explore category dishes"
            )} ${title || ""}`}

            className="
              group/button

              flex

              items-center
              justify-between

              gap-2
              md:gap-4

              min-w-0
            "
          >

            {/* =============================
                CTA TEXT
            ============================== */}

            <span
              className={`
                min-w-0

                font-kufi
                font-medium

                text-tcolor-4
                dark:text-tcolor-1

                transition-colors
                duration-300

                group-hover/button:text-color-1

                ${
                  compact
                    ? `
                      truncate
                      text-sm

                      sm:text-sm
                      md:text-base
                    `
                    : `
                      text-xs

                      sm:text-sm
                      md:text-base
                    `
                }
              `}
            >
              {compact
                ? t(
                    "explore"
                  )
                : t(
                    "explore category dishes"
                  )}
            </span>


            {/* =============================
                ARROW
            ============================== */}

            <motion.div
              whileHover={{
                scale: 1.08,
              }}

              whileTap={{
                scale: 0.95,
              }}

              className={`
                flex

                shrink-0

                items-center
                justify-center

                rounded-full

                bg-color-1/10

                text-color-1

                transition-all
                duration-300

                group-hover/button:bg-color-1
                group-hover/button:text-white

                ${
                  compact
                    ? `
                      w-8
                      h-8

                      sm:w-9
                      sm:h-9

                      md:w-11
                      md:h-11
                    `
                    : `
                      w-10
                      h-10

                      md:w-11
                      md:h-11
                    `
                }
              `}
            >

              <DirectionArrow
                className={`
                  transition-transform
                  duration-300

                  ${
                    isRTL
                      ? `
                        group-hover/button:-translate-x-1
                      `
                      : `
                        group-hover/button:translate-x-1
                      `
                  }

                  ${
                    compact
                      ? `
                        text-xs

                        md:text-lg
                      `
                      : `
                        text-base

                        md:text-lg
                      `
                  }
                `}
              />

            </motion.div>

          </Link>

        </div>

      </div>

    </motion.article>
  );
}


export default MealCategoryCard;